/**
 * usePhaseFeature · resolve a PhaseFeature for the current tenant.
 *
 * Resolution order:
 *   1. companies.feature_overrides[key] (per-tenant override, wins if present)
 *   2. Phase-based default from isPhaseFeatureEnabled(phase, feature)
 *
 * Override keys use the same string as PhaseFeature (e.g. "email_ai") so
 * the admin UI writes one source of truth.
 *
 * Cross-session propagation (2026-05-02 hivesim T4 SERIOUS finding): an admin
 * flipping a flag in AdminHubPage runs invalidateQueries on the *admin's*
 * React Query client, which never reaches the tenant's separate browser
 * session. We subscribe to postgres_changes on the tenant's own companies row
 * so the override resolution refreshes within ~1s of an admin flip instead of
 * waiting on staleTime. If realtime isn't enabled on the table the subscribe
 * silently no-ops and the 60s staleTime still kicks in eventually.
 */
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePhase } from "@/hooks/usePhase";
import {
  isPhaseFeatureEnabled,
  type Phase,
  type PhaseFeature,
} from "@/lib/feature-flags";

interface FeatureResolution {
  enabled: boolean;
  source: "override" | "phase";
  phase: Phase;
}

function resolveFeature(
  phase: Phase,
  feature: PhaseFeature,
  overrides: Record<string, unknown> | null | undefined,
): FeatureResolution {
  const override = overrides?.[feature];
  if (typeof override === "boolean") {
    return { enabled: override, source: "override", phase };
  }
  return {
    enabled: isPhaseFeatureEnabled(phase, feature),
    source: "phase",
    phase,
  };
}

/**
 * Subscribe to postgres_changes on the tenant's companies row and invalidate
 * the override cache on any update.
 *
 * usePhaseFeature(s) mounts simultaneously in multiple places on /dash (Sidebar,
 * DispatcherHomePage, DashboardTour, …) and remounts on route changes. If two
 * callers reuse the same channel name, supabase.channel(name) returns the
 * already-subscribed channel and the second .on() throws
 * "cannot add postgres_changes callbacks after subscribe()", crashing the page.
 * We mint a fresh random id INSIDE the effect — not a stable useId — so every
 * mount cycle gets a unique channel name even if removeChannel() from the
 * previous unmount hasn't fully completed yet.
 */
function useFeatureOverrideRealtime(companyId: string | null | undefined): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (!companyId) return;
    const uniqueId = (typeof crypto !== "undefined" && "randomUUID" in crypto)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(`companies:${companyId}:overrides:${uniqueId}`)
      .on(
        "postgres_changes" as never,
        {
          event: "UPDATE",
          schema: "public",
          table: "companies",
          filter: `id=eq.${companyId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["phase-feature-overrides", companyId] });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [companyId, qc]);
}

/**
 * Single-feature check.
 * Returns { enabled, source, phase, isLoading }.
 * While loading we default to `enabled: false` so gated routes never flash.
 */
export function usePhaseFeature(feature: PhaseFeature) {
  const { companyId } = useAuth();
  const { phase } = usePhase();
  useFeatureOverrideRealtime(companyId);

  const { data: overrides, isLoading } = useQuery({
    queryKey: ["phase-feature-overrides", companyId],
    enabled: !!companyId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("companies")
        .select("feature_overrides")
        .eq("id", companyId!)
        .maybeSingle();
      return (data?.feature_overrides ?? {}) as Record<string, unknown>;
    },
  });

  const resolution = resolveFeature(phase, feature, overrides);
  return {
    ...resolution,
    isLoading: !!companyId && isLoading,
  };
}

/**
 * Batch version — returns a Record<PhaseFeature, boolean>.
 * Use this in nav-computation so we don't stack multiple useQuery calls.
 */
export function usePhaseFeatures<T extends PhaseFeature>(
  features: readonly T[],
): { map: Record<T, boolean>; isLoading: boolean; phase: Phase } {
  const { companyId } = useAuth();
  const { phase } = usePhase();
  useFeatureOverrideRealtime(companyId);

  const { data: overrides, isLoading } = useQuery({
    queryKey: ["phase-feature-overrides", companyId],
    enabled: !!companyId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("companies")
        .select("feature_overrides")
        .eq("id", companyId!)
        .maybeSingle();
      return (data?.feature_overrides ?? {}) as Record<string, unknown>;
    },
  });

  const map = features.reduce((acc, f) => {
    acc[f] = resolveFeature(phase, f, overrides).enabled;
    return acc;
  }, {} as Record<T, boolean>);

  return { map, isLoading: !!companyId && isLoading, phase };
}
